/**
 * Multi-Schedule Grid Component
 * Container for displaying multiple schedules in a timeline view
 */

import React, { useState, useMemo } from 'react'

import {
    TimelineBarItem,
    TimelineDateRange,
    ScheduleGroup,
} from '../../../../types/timeline'
import { generateDateAxis, isToday } from '../utils/dateUtils'
import ScheduleSection from './ScheduleSection'

interface MultiScheduleGridProps {
    schedules: ScheduleGroup[]
    dateRange: TimelineDateRange
    onBarClick?: (item: TimelineBarItem, event: React.MouseEvent) => void
}

// Day abbreviations
const DAY_ABBR = ['S', 'M', 'T', 'W', 'T', 'F', 'S']

const MultiScheduleGrid: React.FC<MultiScheduleGridProps> = ({
    schedules,
    dateRange,
    onBarClick,
}) => {
    // Track expanded state for each schedule
    const [expandedSchedules, setExpandedSchedules] = useState<Set<number>>(
        new Set()
    )

    // Generate dates for the range
    const dates = useMemo(() => generateDateAxis(dateRange), [dateRange])

    // Toggle schedule expansion
    const handleToggle = (scheduleId: number) => {
        setExpandedSchedules((prev) => {
            const next = new Set(prev)
            if (next.has(scheduleId)) {
                next.delete(scheduleId)
            } else {
                next.add(scheduleId)
            }
            return next
        })
    }

    if (schedules.length === 0) {
        return (
            <div className="border border-gray-200 rounded-lg bg-white p-8 text-center">
                <span className="text-gray-400">No schedules found</span>
            </div>
        )
    }

    return (
        <div className="border border-gray-200 rounded-lg overflow-hidden bg-white">
            {/* Date Header Row */}
            <div className="flex items-center bg-gray-50 border-b border-gray-200 sticky top-0 z-10">
                <div className="w-40 px-3 py-2 text-xs font-semibold text-gray-700 border-r border-gray-200 flex-shrink-0">
                    Schedule
                </div>
                <div
                    className="flex-1 grid"
                    style={{
                        gridTemplateColumns: `repeat(${dates.length}, minmax(40px, 1fr))`,
                    }}
                >
                    {dates.map((date, idx) => (
                        <div
                            key={idx}
                            className={`
                                text-center py-2 text-xs border-r border-gray-100 last:border-r-0
                                ${isToday(date) ? 'bg-teal-50' : ''}
                            `}
                        >
                            <div className="text-gray-400">
                                {DAY_ABBR[date.day()]}
                            </div>
                            <div
                                className={`font-medium ${isToday(date) ? 'text-teal-600' : 'text-gray-700'}`}
                            >
                                {date.format('D')}
                            </div>
                        </div>
                    ))}
                </div>
            </div>

            {/* Schedule Sections */}
            {schedules.map((schedule) => (
                <ScheduleSection
                    key={schedule.scheduleId}
                    schedule={schedule}
                    dates={dates}
                    dateRange={dateRange}
                    isExpanded={expandedSchedules.has(schedule.scheduleId)}
                    onToggle={() => handleToggle(schedule.scheduleId)}
                    onBarClick={onBarClick}
                />
            ))}
        </div>
    )
}

export default MultiScheduleGrid
